<template>
  <Dialog
    v-model:visible="aberto"
    :modal="true"
    :dismissableMask="true"
    :draggable="false"
    :style="{ width: '95vw', maxWidth: '1400px' }"
    @hide="fechar()"
  >
    <template #header>
      <h3 class="p-m-0">
        Solicitação RNTRC nº {{ idRntrcSolicitacao }}
        <span
          v-if="entity?.flSituacao"
          class="aet-badge p-ml-2"
          :class="'aet-badge--' + getSituacaoCor(entity.flSituacao)"
          v-text="getSituacaoLabel(entity.flSituacao)"
        />
      </h3>
    </template>

    <div v-if="!entity" class="p-p-3">
      <Skeleton width="100%" height="2rem" class="p-mb-2" />
      <Skeleton width="100%" height="10rem" />
    </div>

    <div v-else class="rntrc-detalhe">
      <!-- Cabeçalho -->
      <div class="p-grid p-m-0">
        <div class="p-col-12 p-md-6 p-lg-4 rntrc-campo">
          <small>Tipo</small>
          <div v-text="getTipoServicoLabel(entity.flTipoServico)" />
        </div>
        <div class="p-col-12 p-md-6 p-lg-4 rntrc-campo">
          <small v-text="labelTransportador" />
          <div>
            <span v-text="formatCpfCnpj(entity.cpfCnpj) || '---'" />
            <span v-if="entity.nomeTransportador">
              - {{ entity.nomeTransportador }}</span
            >
          </div>
        </div>
        <div
          class="p-col-12 p-md-6 p-lg-4 rntrc-campo"
          v-if="amIAllowed('view', 'root-cliente') && entity.cliente"
        >
          <small>Cliente</small>
          <div
            v-text="entity.cliente.idCliente + ' - ' + entity.cliente.nome"
          />
        </div>
        <div class="p-col-12 p-md-6 p-lg-4 rntrc-campo">
          <small>Solicitado em</small>
          <div>
            {{ formatDateTime(entity.dataCadastro) }}
            <span v-if="entity.nomeUsuario"> por {{ entity.nomeUsuario }}</span>
          </div>
        </div>
        <div class="p-col-12 p-md-6 p-lg-4 rntrc-campo">
          <small>Pedido ANTT</small>
          <div>
            <span v-text="entity.idPedidoSitcarga || '---'" />
            <span v-if="entity.protocolo">
              (protocolo {{ entity.protocolo }})</span
            >
          </div>
        </div>
        <template v-if="isConsultaTransportador && entity.rntrcTransportador">
          <div class="p-col-12 p-md-6 p-lg-4 rntrc-campo">
            <small>RNTRC</small>
            <div v-text="entity.rntrcTransportador" />
          </div>
          <div class="p-col-12 p-md-6 p-lg-4 rntrc-campo">
            <small>Nome no RNTRC</small>
            <div v-text="entity.nomeRntrcTransportador || '---'" />
          </div>
          <div class="p-col-12 p-md-6 p-lg-4 rntrc-campo">
            <small>Situação do RNTRC</small>
            <div v-text="entity.situacaoRntrcTransportador || '---'" />
          </div>
        </template>
        <div class="p-col-12 p-md-6 p-lg-4 rntrc-campo">
          <small>Responsável pelo pedido</small>
          <div>Procurador da GovPass</div>
        </div>
        <div class="p-col-12 p-md-6 p-lg-4 rntrc-campo">
          <small>Valor GovPass</small>
          <div
            v-text="
              formatMoeda(entity.valorTotalServico ?? entity.valorServico) ||
                '---'
            "
          />
        </div>
        <div
          class="p-col-12 p-md-6 p-lg-4 rntrc-campo"
          v-if="entity.dataPagamentoInformado"
        >
          <small>Pagamento informado em</small>
          <div v-text="formatDateTime(entity.dataPagamentoInformado)" />
        </div>
        <div
          class="p-col-12 p-md-6 p-lg-4 rntrc-campo"
          v-if="entity.dataPagamentoConfirmado"
        >
          <small>Pagamento confirmado pela ANTT em</small>
          <div v-text="formatDateTime(entity.dataPagamentoConfirmado)" />
        </div>
        <div
          class="p-col-12 p-md-6 p-lg-4 rntrc-campo"
          v-if="entity.proximaVerificacaoPagamento && emPagamento"
        >
          <small>Próxima verificação do pagamento</small>
          <div v-text="formatDateTime(entity.proximaVerificacaoPagamento)" />
        </div>
        <div
          class="p-col-12 p-md-6 p-lg-4 rntrc-campo"
          v-if="entity.dataConclusao"
        >
          <small>Concluída em</small>
          <div v-text="formatDateTime(entity.dataConclusao)" />
        </div>
        <div
          class="p-col-12 p-md-6 p-lg-4 rntrc-campo"
          v-if="entity.flProsseguirParcial"
        >
          <small>Placas com pendência</small>
          <div>Prosseguir com as demais placas</div>
        </div>
        <div class="p-col-12 rntrc-campo" v-if="entity.observacao">
          <small>Observação</small>
          <div v-text="entity.observacao" />
        </div>
        <div
          class="p-col-12 p-md-6 p-lg-4 rntrc-campo"
          v-if="amIAllowed('view', 'root-cliente') && entity.statusRobo"
        >
          <small>Robô</small>
          <div>
            {{ entity.statusRobo }}
            <span v-if="entity.tentativasRobo">
              ({{ entity.tentativasRobo }} tentativa(s))</span
            >
          </div>
        </div>
      </div>

      <!-- Cancelamento -->
      <div
        class="rntrc-bloco rntrc-bloco--muted"
        v-if="entity.flSituacao === 'CAN'"
      >
        <h4>Cancelamento</h4>
        <div>
          <b
            v-text="
              getMotivoCancelamentoLabel(entity.flMotivoCancelamento) ||
                'Cancelada'
            "
          />
          <span v-if="entity.dataCancelamento">
            em {{ formatDateTime(entity.dataCancelamento) }}</span
          >
        </div>
        <div
          v-if="entity.msgCliente"
          class="p-mt-1"
          v-text="entity.msgCliente"
        />
      </div>

      <!-- Consulta de transportador sem cadastro: o próximo passo é cadastrar -->
      <div
        class="rntrc-bloco rntrc-bloco--warning"
        v-if="semCadastroNaConsulta"
      >
        <h4>Transportador sem cadastro no RNTRC</h4>
        <div>
          Este transportador ainda não tem cadastro na ANTT. Para realizar o
          transporte remunerado de cargas, ele precisa ser cadastrado
          <span
            v-text="ehCpf ? 'como TAC (autônomo).' : 'como ETC (empresa).'"
          />
        </div>
        <div class="p-mt-3" v-if="podeNaSolicitacao('fazer-cadastro', entity)">
          <Button
            label="Fazer cadastro na ANTT"
            icon="fas fa-user-plus"
            class="p-button-rounded"
            @click="emitAcao('fazer-cadastro')"
          />
        </div>
      </div>

      <!-- Desfechos definitivos do SITCarga -->
      <div
        class="rntrc-bloco rntrc-bloco--muted"
        v-else-if="['PSC', 'TSC', 'CRD', 'CIR'].indexOf(entity.flSituacao) >= 0"
      >
        <h4 v-text="getSituacaoLabel(entity.flSituacao)" />
        <div v-if="entity.msgCliente" v-text="entity.msgCliente" />
        <div class="p-mt-1">
          A ANTT recusou o pedido de forma definitiva; repetir a mesma
          solicitação não muda o resultado.
        </div>
      </div>

      <!-- Placa presa em contrato de outro transportador -->
      <div
        class="rntrc-bloco rntrc-bloco--warning"
        v-if="entity.flSituacao === 'EXN'"
      >
        <h4>Exclusão da frota atual necessária</h4>
        <div>
          Para incluir, primeiro é preciso excluir da frota atual:
        </div>
        <ul class="p-my-2">
          <li v-for="v in placasPresas" :key="v.idRntrcSolicitacaoVeiculo">
            <b v-text="formatPlaca(v.placa)" /> — com
            {{ v.nomeTransportadorAtual }}
            <span v-if="v.cpfCnpjAtual">
              ({{ formatCpfCnpj(v.cpfCnpjAtual) }})</span
            >
          </li>
        </ul>
        <div>
          A GovPass faz a exclusão (cobrada como uma exclusão) e, assim que o
          PIX dela for pago, segue com esta inclusão automaticamente.
        </div>
        <div
          class="p-mt-3"
          v-if="podeNaSolicitacao('excluir-frota-atual', entity)"
        >
          <Button
            label="Prosseguir com a exclusão"
            icon="fas fa-exchange-alt"
            class="p-button-rounded"
            @click="emitAcao('excluir-frota-atual')"
          />
        </div>
      </div>

      <!-- Exclusão da frota atual em andamento (escondida da lista) -->
      <div
        class="rntrc-bloco rntrc-bloco--warning"
        v-if="entity.flSituacao === 'AEX'"
      >
        <h4>Excluindo da frota atual</h4>
        <div
          v-for="e in entity.exclusoes ?? []"
          :key="e.idRntrcSolicitacao"
          class="p-mb-3"
        >
          <div>
            Frota de <b v-text="e.nomeTransportador" />:
            <span
              class="aet-badge"
              :class="'aet-badge--' + getSituacaoCor(e.flSituacao)"
              v-text="getSituacaoLabel(e.flSituacao)"
            />
          </div>
          <rntrc-pix-bloco
            v-if="
              (e.flSituacao === 'PAG' || e.flSituacao === 'PGI') && e.codigoPix
            "
            class="p-mt-2"
            :codigoPix="e.codigoPix ?? undefined"
            :valorPix="e.valorPix ?? undefined"
            :dataVencimentoPix="e.dataVencimentoPix ?? undefined"
          />
        </div>
        <div>Depois do pagamento da exclusão, a inclusão continua sozinha.</div>
      </div>

      <!-- Cancelamento na ANTT em andamento -->
      <div class="rntrc-bloco rntrc-bloco--danger" v-if="entity.flAcaoRobo">
        <h4>Cancelamento na ANTT em andamento</h4>
        <div v-if="entity.flAcaoRobo === 'CPL'">
          O robô vai tirar do pedido {{ entity.idPedidoSitcarga }} as placas
          <b v-text="placasEmCancelamento" /> e gerar um PIX novo com as demais.
        </div>
        <div v-else>
          O robô vai cancelar o pedido {{ entity.idPedidoSitcarga }} inteiro na
          ANTT; o serviço não é cobrado.
        </div>
        <div class="p-mt-1">O PIX anterior não vale mais: não pague.</div>
      </div>

      <!-- PIX -->
      <div class="rntrc-bloco rntrc-bloco--warning" v-if="mostrarPix">
        <h4>PIX</h4>
        <rntrc-pix-bloco
          :codigoPix="entity.codigoPix ?? undefined"
          :valorPix="entity.valorPix ?? undefined"
          :dataVencimentoPix="entity.dataVencimentoPix ?? undefined"
        />
      </div>

      <!-- Erro -->
      <div
        class="rntrc-bloco rntrc-bloco--danger"
        v-if="entity.flSituacao === 'ERR'"
      >
        <h4>Erro</h4>
        <div
          v-if="entity.codigoErro"
          class="p-mb-1"
          v-text="getCodigoErroLabel(entity.codigoErro)"
        />
        <div
          class="rntrc-texto-longo"
          v-text="
            entity.msgCliente || 'O robô não conseguiu concluir o pedido.'
          "
        />
        <div
          class="rntrc-texto-longo p-mt-2"
          v-if="amIAllowed('view', 'root-cliente') && entity.logErro"
        >
          <small><b>Log:</b> {{ entity.logErro }}</small>
        </div>
        <div class="p-mt-2" v-if="entity.possuiImagemErro">
          <Button
            v-if="!imagemErroUrl"
            label="Ver imagem do erro"
            icon="fas fa-exclamation-triangle"
            class="p-button-rounded p-button-text p-button-danger"
            :loading="carregandoImagemErro"
            @click="carregarImagemErro()"
          />
          <a v-else :href="imagemErroUrl" target="_blank" rel="noopener">
            <img
              class="rntrc-imagem-erro"
              :src="imagemErroUrl"
              alt="Imagem do Erro"
            />
          </a>
        </div>
      </div>

      <!-- Veículos -->
      <div class="rntrc-bloco" v-if="entity.veiculos && entity.veiculos.length">
        <div class="rntrc-bloco-titulo">
          <h4>Veículos</h4>
          <Button
            v-if="isConsulta && arquivosParaBaixar.length > 1"
            :label="
              baixandoTodos
                ? 'Preparando o .zip...'
                : 'Baixar todos (' +
                  arquivosParaBaixar.length +
                  ' PDFs em .zip)'
            "
            :icon="baixandoTodos ? 'pi pi-spin pi-spinner' : 'pi pi-download'"
            class="p-button-rounded p-button-outlined p-button-sm rntrc-baixar-todos"
            :disabled="baixandoTodos || !podeNaSolicitacao('download', entity)"
            @click="baixarTodos()"
          />
        </div>
        <DataTable
          :value="entity.veiculos"
          class="p-datatable-sm"
          responsiveLayout="stack"
          breakpoint="60rem"
          :stripedRows="true"
        >
          <Column header="Placa" style="width: 7rem">
            <template #body="{ data }">
              <b v-text="formatPlaca(data.placa)" />
            </template>
          </Column>
          <Column field="renavam" header="Renavam" v-if="!isConsulta" />
          <Column header="Tipo" v-if="!isConsulta">
            <template #body="{ data }">
              {{ getServicoVeiculoLabel(data.flServicoVeiculo) }}
            </template>
          </Column>
          <Column
            header="Arrendante (proprietário)"
            v-if="entity.flTipoServico === 'IAR'"
          >
            <template #body="{ data }">
              <span
                v-if="data.cpfCnpjArrendante"
                class="rntrc-uma-linha"
                v-tooltip.top="data.nomeArrendante || ''"
                v-text="formatCpfCnpj(data.cpfCnpjArrendante)"
              />
            </template>
          </Column>
          <Column
            field="servicoSite"
            header="Serviço lido no site"
            v-if="!isConsulta"
          />
          <Column header="Valor ANTT" v-if="!isConsulta">
            <template #body="{ data }">
              {{ formatMoeda(data.valorSite) }}
            </template>
          </Column>
          <Column header="Valor GovPass" v-if="!isConsulta">
            <template #body="{ data }">
              {{ formatMoeda(data.valorServico) }}
            </template>
          </Column>
          <Column header="Incluído?" v-if="!isConsulta">
            <template #body="{ data }">
              <span
                v-if="data.flExclusaoNecessaria"
                class="aet-badge aet-badge--warning"
                v-tooltip.top="
                  'Presa em contrato de ' + (data.nomeTransportadorAtual ?? '')
                "
                >Excluir antes</span
              >
              <span
                v-else-if="data.dataCancelamento"
                class="aet-badge aet-badge--muted"
                v-tooltip.top="
                  'Cancelada na ANTT em ' +
                    formatDateTime(data.dataCancelamento)
                "
                >Cancelada</span
              >
              <span
                v-else-if="data.flCancelamentoSolicitado"
                class="aet-badge aet-badge--danger"
                >Cancelando</span
              >
              <span
                v-else-if="data.flIncluido === true"
                class="aet-badge aet-badge--success"
                >Sim</span
              >
              <span
                v-else-if="data.flIncluido === false"
                class="aet-badge aet-badge--danger"
                >Não</span
              >
              <span v-else>---</span>
            </template>
          </Column>
          <Column header="Transportador atual" v-if="isConsulta">
            <template #body="{ data }">
              <span v-if="data.cpfCnpjAtual || data.nomeTransportadorAtual">
                {{ formatCpfCnpj(data.cpfCnpjAtual) }}
                <span v-if="data.nomeTransportadorAtual">
                  - {{ data.nomeTransportadorAtual }}</span
                >
              </span>
              <span v-else>---</span>
            </template>
          </Column>
          <Column header="Veículo (extrato)" v-if="isConsulta">
            <template #body="{ data }">
              <span v-if="data.flServicoVeiculoAtual || data.renavamAtual">
                {{ getServicoVeiculoLabel(data.flServicoVeiculoAtual) }}
                <span v-if="data.categoriaAtual">
                  ({{ data.categoriaAtual }})</span
                >
                <br />
                <small class="rntrc-texto-suave">
                  <span v-if="data.renavamAtual"
                    >Renavam {{ data.renavamAtual }}</span
                  >
                  <span v-if="data.propriedadeAtual">
                    · {{ data.propriedadeAtual.toLowerCase() }}</span
                  >
                </small>
              </span>
              <span v-else>---</span>
            </template>
          </Column>
          <Column field="rntrcAtual" header="RNTRC" v-if="isConsulta" />
          <Column field="situacaoAtual" header="Situação" v-if="isConsulta" />
          <!-- Consulta de veículo: os documentos do dono de cada placa, na própria linha -->
          <Column header="Documento" v-if="isConsulta">
            <template #body="{ data }">
              <span
                class="rntrc-docs-linha"
                v-if="data.possuiExtrato || data.possuiCertificado"
              >
                <Button
                  v-if="data.possuiExtrato"
                  label="Extrato"
                  icon="far fa-file-pdf"
                  class="p-button-text p-button-sm rntrc-doc-linha"
                  :disabled="!podeNaSolicitacao('download', entity)"
                  @click="abrirDocumentoVeiculo(data, 'EXT')"
                  @click.middle.prevent.stop="
                    abrirDocumentoVeiculo(data, 'EXT', true)
                  "
                />
                <Button
                  v-if="data.possuiCertificado"
                  label="Certificado"
                  icon="far fa-file-pdf"
                  class="p-button-text p-button-sm rntrc-doc-linha"
                  :disabled="!podeNaSolicitacao('download', entity)"
                  @click="abrirDocumentoVeiculo(data, 'CER')"
                  @click.middle.prevent.stop="
                    abrirDocumentoVeiculo(data, 'CER', true)
                  "
                />
              </span>
              <span v-else>---</span>
            </template>
          </Column>
          <Column field="motivo" header="Motivo" v-else />
        </DataTable>
      </div>

      <!-- Custo total: a solicitação e as exclusões da frota atual feitas no caminho -->
      <div class="rntrc-bloco" v-if="custoDaOperacao">
        <h4>Custo total da operação</h4>
        <p class="rntrc-texto-suave p-mt-0 p-mb-2">
          Para incluir, a placa precisou antes ser excluída da frota em que
          estava. Essa exclusão faz parte do custo desta solicitação.
        </p>
        <table class="rntrc-custos">
          <thead>
            <tr>
              <th>Etapa</th>
              <th>Taxa ANTT</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="l in custoDaOperacao.linhas" :key="l.chave">
              <td>
                {{ l.descricao }}
                <span
                  v-if="l.situacao && l.situacao !== 'CON'"
                  class="aet-badge p-ml-1"
                  :class="'aet-badge--' + getSituacaoCor(l.situacao)"
                  v-text="getSituacaoLabel(l.situacao)"
                />
              </td>
              <td v-text="formatMoeda(l.antt) || '---'" />
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th>Total</th>
              <th v-text="formatMoeda(custoDaOperacao.antt)" />
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Documentos (na consulta sem cadastro o caminho é o cadastro, não o print) -->
      <div
        class="rntrc-bloco"
        v-if="documentos.length && !semCadastroNaConsulta"
      >
        <div class="rntrc-bloco-titulo">
          <h4>Documentos</h4>
          <Button
            v-if="!isConsulta && arquivosParaBaixar.length > 1"
            :label="
              baixandoTodos
                ? 'Preparando o .zip...'
                : 'Baixar todos (' +
                  arquivosParaBaixar.length +
                  ' PDFs em .zip)'
            "
            :icon="baixandoTodos ? 'pi pi-spin pi-spinner' : 'pi pi-download'"
            class="p-button-rounded p-button-outlined p-button-sm rntrc-baixar-todos"
            :disabled="baixandoTodos || !podeNaSolicitacao('download', entity)"
            @click="baixarTodos()"
          />
        </div>
        <button-group>
          <Button
            v-for="doc in documentos"
            :key="doc.flag"
            :label="doc.descricao"
            icon="far fa-file-pdf"
            class="p-button-rounded p-button-outlined"
            :disabled="!podeNaSolicitacao('download', entity)"
            @click="abrirDocumento(doc.flag)"
            @click.middle.prevent.stop="abrirDocumento(doc.flag, true)"
          />
        </button-group>
      </div>
    </div>

    <rntrc-outro-servico
      v-if="outroServicoAberto && entity"
      :solicitacao="entity"
      @close="outroServicoAberto = false"
    />

    <template #footer>
      <div class="p-d-flex p-flex-wrap p-jc-end" style="gap: 0.5rem">
        <template v-if="entity">
          <Button
            v-if="podeNaSolicitacao('excluir-frota-atual', entity)"
            label="Prosseguir com a exclusão"
            icon="fas fa-exchange-alt"
            class="p-button-rounded"
            @click="emitAcao('excluir-frota-atual')"
          />
          <Button
            v-if="podeNaSolicitacao('cancelar-placas-antt', entity)"
            label="Cancelar placas"
            icon="fas fa-minus-circle"
            class="p-button-rounded p-button-danger p-button-outlined"
            @click="emitAcao('cancelar-placas-antt')"
          />
          <Button
            v-if="podeNaSolicitacao('cancelar-pedido-antt', entity)"
            label="Cancelar pedido"
            icon="fas fa-ban"
            class="p-button-rounded p-button-danger p-button-outlined"
            @click="emitAcao('cancelar-pedido-antt')"
          />
          <Button
            v-if="podeNaSolicitacao('confirmar-pagamento', entity)"
            label="Confirmar pagamento"
            icon="fas fa-check"
            class="p-button-rounded p-button-success"
            @click="emitAcao('confirmar-pagamento')"
          />
          <Button
            v-if="podeNaSolicitacao('reenviar', entity)"
            label="Reenviar"
            icon="fas fa-redo"
            class="p-button-rounded"
            @click="emitAcao('reenviar')"
          />
          <Button
            v-if="podeNaSolicitacao('editar', entity)"
            label="Editar"
            icon="pi pi-pencil"
            class="p-button-rounded p-button-outlined"
            @click="emitAcao('editar')"
          />
          <Button
            v-if="podeNaSolicitacao('outro-servico', entity)"
            label="Realizar outro serviço"
            icon="pi pi-arrow-right"
            iconPos="right"
            class="p-button-rounded"
            @click="outroServicoAberto = true"
          />
          <Button
            v-if="podeNaSolicitacao('incluir-destino', entity)"
            label="Incluir no destino"
            icon="fas fa-exchange-alt"
            class="p-button-rounded p-button-info"
            @click="emitAcao('incluir-destino')"
          />
          <Button
            v-if="podeNaSolicitacao('cancelar', entity)"
            label="Cancelar solicitação"
            icon="fas fa-ban"
            class="p-button-rounded p-button-danger p-button-outlined"
            @click="emitAcao('cancelar')"
          />
        </template>
        <Button
          v-if="podeCriar"
          label="Nova solicitação"
          icon="pi pi-plus"
          class="p-button-rounded"
          @click="novaSolicitacao()"
        />
        <Button
          label="Fechar"
          class="p-button-rounded p-button-text"
          @click="fechar()"
        />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  watch
} from "vue";
import RntrcPixBloco from "./RntrcPixBloco.vue";
import RntrcOutroServico from "./RntrcOutroServico.vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { formatDateTime } from "../../modules/utils";
import { useToast } from "../../modules/message";
import { useRouter } from "vue-router";
import {
  useRntrcSolicitacaoService,
  podeNaSolicitacao
} from "../../modules/business/rntrc/rntrcSolicitacaoBusiness";
import {
  DocumentoRntrc,
  FlagOption,
  RntrcSolicitacaoPayload,
  RntrcSolicitacaoVeiculoPayload,
  SituacaoRntrc,
  formatCpfCnpj,
  formatMoeda,
  formatPlaca,
  getCodigoErroLabel,
  getDocumentoLabel,
  getMotivoCancelamentoLabel,
  getServicoVeiculoLabel,
  getSituacaoCor,
  getSituacaoLabel,
  getTipoServicoLabel
} from "../../modules/business/rntrc/rntrcTypes";

interface RntrcDetalheState {
  aberto: boolean;
  entity: RntrcSolicitacaoPayload | null;
  imagemErroUrl: string | null;
  carregandoImagemErro: boolean;
  outroServicoAberto: boolean;
  /** Baixar todos: montando e gravando o .zip. */
  baixandoTodos: boolean;
}

/** Uma linha do custo total: a própria solicitação ou uma exclusão da frota atual. */
interface LinhaCusto {
  chave: string;
  descricao: string;
  situacao?: SituacaoRntrc | null;
  antt: number | null;
}

/** Um PDF do "Baixar todos": da solicitação ou de uma placa (consulta de veículo). */
interface ArquivoParaBaixar {
  tipo: DocumentoRntrc;
  veiculo?: RntrcSolicitacaoVeiculoPayload;
}

/** O "Salvar como" da File System Access API (Chrome e Edge). */
interface ArquivoGravavel {
  write(dados: BufferSource | Blob): Promise<void>;
  close(): Promise<void>;
}

interface ArquivoEscolhido {
  createWritable(): Promise<ArquivoGravavel>;
}

type JanelaComSalvarComo = Window & {
  showSaveFilePicker?: (opcoes: {
    suggestedName?: string;
    startIn?: string;
    types?: Array<{
      description: string;
      accept: Record<string, Array<string>>;
    }>;
  }) => Promise<ArquivoEscolhido>;
};

export default defineComponent({
  emits: ["close", "acao"],
  setup(props, { emit }) {
    const state = reactive<RntrcDetalheState>({
      aberto: true,
      entity: null,
      imagemErroUrl: null,
      carregandoImagemErro: false,
      outroServicoAberto: false,
      baixandoTodos: false
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const router = useRouter();

    const podeCriar = computed(() => !!amIAllowed("create", "rntrc"));

    /** Depois de ver uma solicitação, o cliente já parte para a próxima. */
    const novaSolicitacao = () => {
      router.push({ name: "rntrc-solicitacao-new" });
    };

    const fechar = () => {
      state.aberto = false;
      emit("close", props.idRntrcSolicitacao);
    };

    const carregar = () => {
      const { data, buscar } = useRntrcSolicitacaoService();
      buscar(props.idRntrcSolicitacao)
        .then(() => {
          state.entity = data.value ?? null;
        })
        .catch(() => {
          fechar();
        });
    };

    const emitAcao = (acao: string) => {
      emit("acao", acao, state.entity);
    };

    const isConsulta = computed(() => state.entity?.flTipoServico === "CVE");

    const isConsultaTransportador = computed(
      () => state.entity?.flTipoServico === "CTR"
    );

    const semCadastroNaConsulta = computed(
      () => isConsultaTransportador.value && state.entity?.flSituacao === "TSC"
    );

    const ehCpf = computed(
      () => (state.entity?.cpfCnpj ?? "").replace(/\D/g, "").length === 11
    );

    const emPagamento = computed(
      () =>
        state.entity?.flSituacao === "PAG" || state.entity?.flSituacao === "PGI"
    );

    const mostrarPix = computed(
      () =>
        emPagamento.value ||
        (!!state.entity?.codigoPix &&
          state.entity?.flSituacao !== "CON" &&
          state.entity?.flSituacao !== "CAN")
    );

    const placasPresas = computed(() =>
      (state.entity?.veiculos ?? []).filter((v) => v.flExclusaoNecessaria)
    );

    const placasEmCancelamento = computed(() =>
      (state.entity?.veiculos ?? [])
        .filter((v) => v.flCancelamentoSolicitado && !v.dataCancelamento)
        .map((v) => formatPlaca(v.placa))
        .join(", ")
    );

    const labelTransportador = computed(() => {
      if (state.entity?.flTipoServico === "EXC") {
        return "CPF/CNPJ de quem tem a placa hoje";
      } else if (state.entity?.flTipoServico === "IAR") {
        return "Transportador (arrendatário)";
      }
      return "Transportador";
    });

    const documentos = computed(() => {
      const docs = [] as Array<FlagOption<DocumentoRntrc>>;
      const e = state.entity;
      // consulta de veículo: extrato e certificado ficam na linha de cada placa
      const porPlaca =
        isConsulta.value &&
        (e?.veiculos ?? []).some((v) => v.possuiExtrato || v.possuiCertificado);
      if (e?.possuiExtrato && !porPlaca) {
        docs.push({ flag: "EXT", descricao: getDocumentoLabel("EXT") });
      }
      if (e?.possuiCertificado && !porPlaca) {
        docs.push({ flag: "CER", descricao: getDocumentoLabel("CER") });
      }
      if (e?.possuiRelatorio) {
        docs.push({ flag: "REL", descricao: getDocumentoLabel("REL") });
      }
      if (e?.possuiComprovante) {
        docs.push({ flag: "COM", descricao: getDocumentoLabel("COM") });
      }
      return docs;
    });

    const abrirDocumento = (tipo: DocumentoRntrc, newTab?: boolean) => {
      const { abrirDocumento } = useRntrcSolicitacaoService();
      abrirDocumento(props.idRntrcSolicitacao, tipo, newTab);
    };

    const abrirDocumentoVeiculo = (
      v: RntrcSolicitacaoVeiculoPayload,
      tipo: "EXT" | "CER",
      newTab?: boolean
    ) => {
      if (!v.idRntrcSolicitacaoVeiculo) {
        return;
      }
      const { abrirDocumentoVeiculo } = useRntrcSolicitacaoService();
      abrirDocumentoVeiculo(
        props.idRntrcSolicitacao,
        v.idRntrcSolicitacaoVeiculo,
        tipo,
        newTab
      );
    };

    /** As placas que os documentos do pedido cobrem: as incluídas; nenhuma marcada, todas. */
    const placasDosDocumentos = computed(() => {
      const veiculos = state.entity?.veiculos ?? [];
      const incluidas = veiculos.filter(
        (v) => v.flIncluido !== false && !v.dataCancelamento
      );
      return incluidas.length ? incluidas : veiculos;
    });

    /**
     * Tudo o que o .zip leva, com o nome que a API dá (PLACA-extrato.pdf / PLACA-certificado.pdf):
     * na consulta de veículo, os de cada placa; nos demais, o extrato e o certificado do
     * transportador uma vez por placa do pedido, e os outros documentos uma vez.
     */
    const arquivosParaBaixar = computed<Array<ArquivoParaBaixar>>(() => {
      const lista = [] as Array<ArquivoParaBaixar>;
      if (isConsulta.value) {
        (state.entity?.veiculos ?? []).forEach((v) => {
          if (v.possuiExtrato) {
            lista.push({ tipo: "EXT", veiculo: v });
          }
          if (v.possuiCertificado) {
            lista.push({ tipo: "CER", veiculo: v });
          }
        });
      }
      documentos.value.forEach((d) => {
        const porPlaca =
          !isConsulta.value && (d.flag === "EXT" || d.flag === "CER");
        if (porPlaca && placasDosDocumentos.value.length) {
          placasDosDocumentos.value.forEach((v) =>
            lista.push({ tipo: d.flag, veiculo: v })
          );
        } else {
          lista.push({ tipo: d.flag });
        }
      });
      return lista;
    });

    const nomeDoZip = () =>
      "rntrc-" + props.idRntrcSolicitacao + "-documentos.zip";

    /** Download comum do arquivo que já está na memória. */
    const baixarComoDownload = (dados: ArrayBuffer, nome: string) => {
      const url = URL.createObjectURL(
        new Blob([dados], { type: "application/zip" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = nome;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    };

    /**
     * Um .zip com todos os PDFs (cada um com o nome da placa). Chrome e Edge abrem o "Salvar
     * como" para escolher onde gravar; nos demais navegadores vai como um download comum.
     */
    const baixarTodos = async () => {
      const salvarComo = (window as JanelaComSalvarComo).showSaveFilePicker;
      if (!salvarComo) {
        state.baixandoTodos = true;
        useRntrcSolicitacaoService()
          .abrirZipDocumentos(props.idRntrcSolicitacao)
          .finally(() => (state.baixandoTodos = false));
        return;
      }
      let destino: ArquivoEscolhido;
      try {
        // a janela abre antes do download: o navegador só a permite logo depois do clique
        destino = await salvarComo.call(window, {
          suggestedName: nomeDoZip(),
          startIn: "downloads",
          types: [
            {
              description: "Arquivo .zip",
              accept: { "application/zip": [".zip"] }
            }
          ]
        });
      } catch (e) {
        // fechou o "Salvar como" sem escolher
        return;
      }
      state.baixandoTodos = true;
      const service = useRntrcSolicitacaoService();
      try {
        await service.buscarZipDocumentos(props.idRntrcSolicitacao);
      } catch (e) {
        // o aviso do erro da API já aparece
        state.baixandoTodos = false;
        return;
      }
      const zip = service.data.value as ArrayBuffer;
      try {
        const gravavel = await destino.createWritable();
        await gravavel.write(zip);
        await gravavel.close();
        useToast().success(
          arquivosParaBaixar.value.length + " documento(s) salvos no .zip."
        );
      } catch (e) {
        // o navegador recusou gravar no local escolhido: o .zip já está pronto, vai como download
        baixarComoDownload(zip, nomeDoZip());
        useToast().warning(
          "O navegador não deixou gravar no local escolhido; o .zip foi para a pasta de downloads dele."
        );
      } finally {
        state.baixandoTodos = false;
      }
    };

    /**
     * Custo total quando a solicitação precisou de exclusão da frota atual: a taxa da ANTT
     * (o valor do site, que é o cobrado do cliente) dela e de cada exclusão feita no caminho.
     */
    const custoDaOperacao = computed(() => {
      const e = state.entity;
      const exclusoes = (e?.exclusoes ?? []).filter(
        (x) =>
          x.flSituacao !== "CAN" && x.flSituacao !== "ERR" && x.valorPix != null
      );
      if (!e || !exclusoes.length) {
        return null;
      }
      const linhas = [
        {
          chave: "solicitacao",
          descricao:
            getTipoServicoLabel(e.flTipoServico) +
            " (nº " +
            e.idRntrcSolicitacao +
            ")",
          situacao: e.flSituacao,
          antt: e.valorPix ?? null
        }
      ] as Array<LinhaCusto>;
      exclusoes.forEach((x) => {
        const placas = (x.veiculos ?? []).map((v) => formatPlaca(v.placa));
        linhas.push({
          chave: "exclusao-" + x.idRntrcSolicitacao,
          descricao: "Exclusão da frota atual — " + placas.join(", "),
          situacao: x.flSituacao,
          antt: x.valorPix ?? null
        });
      });
      return {
        linhas,
        antt: linhas.reduce((total, l) => total + (l.antt ?? 0), 0)
      };
    });

    const carregarImagemErro = () => {
      const { data, linkDocumento } = useRntrcSolicitacaoService();
      state.carregandoImagemErro = true;
      linkDocumento(props.idRntrcSolicitacao, "ERR")
        .then(() => {
          state.imagemErroUrl = data.value ?? null;
        })
        .finally(() => {
          state.carregandoImagemErro = false;
        });
    };

    // a lista incrementa a versão depois de uma ação (confirmar, reenviar, cancelar)
    watch(
      () => props.versao,
      () => carregar()
    );

    onMounted(() => {
      carregar();
    });

    return {
      amIAllowed,
      podeNaSolicitacao,
      fechar,
      emitAcao,
      isConsulta,
      isConsultaTransportador,
      semCadastroNaConsulta,
      ehCpf,
      emPagamento,
      mostrarPix,
      placasEmCancelamento,
      placasPresas,
      labelTransportador,
      documentos,
      abrirDocumento,
      abrirDocumentoVeiculo,
      arquivosParaBaixar,
      baixarTodos,
      custoDaOperacao,
      podeCriar,
      novaSolicitacao,
      carregarImagemErro,
      formatDateTime,
      formatCpfCnpj,
      formatMoeda,
      formatPlaca,
      getCodigoErroLabel,
      getMotivoCancelamentoLabel,
      getServicoVeiculoLabel,
      getSituacaoCor,
      getSituacaoLabel,
      getTipoServicoLabel,
      ...toRefs(state)
    };
  },
  components: {
    RntrcPixBloco,
    RntrcOutroServico
  },
  props: {
    idRntrcSolicitacao: {
      type: Number,
      required: true
    },
    versao: {
      type: Number,
      default: 0
    }
  }
});
</script>

<style scoped lang="scss">
// Documentos na linha de cada placa (consulta de veículo): Extrato e Certificado lado a lado
.rntrc-docs-linha {
  display: flex;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: $space-1;
}

// O .p-button global tem min-width de 10rem; aqui o botão ocupa só o próprio texto.
.rntrc-docs-linha .p-button.rntrc-doc-linha {
  min-width: auto;
  width: auto;
  padding: $space-1 $space-2;
}

.rntrc-campo {
  padding: $space-1 $space-2;

  small {
    color: $text-muted;
  }
}

.rntrc-bloco {
  margin-top: $space-4;
  padding: $space-3 $space-4;
  border: 1px solid $border-color-light;
  border-radius: $radius-md;

  h4 {
    margin: 0 0 $space-2 0;
  }

  &--warning {
    border-left: 4px solid $warning;
  }

  &--danger {
    border-left: 4px solid $danger;
  }

  &--muted {
    border-left: 4px solid $text-muted;
    background: $bg-surface-secondary;
  }
}

.rntrc-bloco-titulo {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: $space-2;
  margin-bottom: $space-2;

  h4 {
    margin: 0;
  }
}

// O .p-button global tem min-width de 10rem; aqui o botão ocupa só o próprio texto.
.rntrc-bloco-titulo .p-button.rntrc-baixar-todos {
  min-width: auto;
  width: auto;
}

.rntrc-uma-linha {
  white-space: nowrap;
}

.rntrc-custos {
  width: 100%;
  border-collapse: collapse;
  font-size: $font-size-sm;

  th,
  td {
    padding: $space-2 $space-3;
    text-align: right;
    border-bottom: 1px solid $border-color-light;
    white-space: nowrap;
  }

  th:first-child,
  td:first-child {
    text-align: left;
    white-space: normal;
  }

  thead th {
    color: $text-muted;
    font-weight: $font-weight-semibold;
  }

  tfoot th {
    border-bottom: none;
    font-weight: $font-weight-bold;
  }
}

.rntrc-texto-suave {
  color: $text-muted;
}

.rntrc-texto-longo {
  white-space: pre-line;
  word-break: break-word;
}

.rntrc-imagem-erro {
  width: 100%;
  border: 1px solid $border-color-light;
  border-radius: $radius-md;
}
</style>
